import axios from "axios";
import config from "../config";
import { mockGenerateNames, mockCheckDomain, mockSearchTrademarks } from "./mockData";

const apiUrl = config.apiUrl;

// Simulate network delay for mock responses (300-600ms)
const mockDelay = () => new Promise(resolve => setTimeout(resolve, 300 + Math.random() * 300));

/**
 * Generate domain names based on keywords and options
 */
export const generateNames = async (params: {
  keywords: string[];
  tone?: string;
  length?: string;
  tlds?: string[];
  // Enhanced parameters for sophisticated control
  industry?: string;
  target_audience?: string;
  naming_strategy?: string[];
  brand_personality?: string[];
  creativity_level?: string;
  avoid_patterns?: string[];
  priority_dimensions?: string[];
  cultural_style?: string;
  international_focus?: boolean;
}) => {
  if (config.useMockData) {
    await mockDelay();
    return { data: mockGenerateNames(params) };
  }
  
  return axios.post(`${apiUrl}/generate-names`, params);
};

/**
 * Generate domain names with simple rules (no scoring)
 */
export const generateNamesSimple = async (params: {
  keywords: string[];
  tone?: string;
  rules?: string[];
  tld?: string;
}) => {
  if (config.useMockData) {
    await mockDelay();
    return { data: mockGenerateNames(params) };
  }
  
  return axios.post(`${apiUrl}/generate-names-simple`, params);
};

/**
 * Check if a domain is available
 */
export const checkDomain = async (params: {
  name: string;
  tld: string;
}) => {
  if (config.useMockData) {
    await mockDelay();
    return { data: mockCheckDomain(params) };
  }
  
  return axios.get(`${apiUrl}/check-domain`, { params });
};

/**
 * Bulk check domain availability for multiple TLDs
 */
export const checkDomainsMultipleTLDs = async (params: {
  name: string;
  tlds: string[];
}) => {
  const results: Record<string, boolean | null> = {};
  
  for (const tld of params.tlds) {
    try {
      const res = await checkDomain({ name: params.name, tld });
      results[tld] = res.data.available;
    } catch (err) {
      console.error(`Failed to check ${params.name}${tld}:`, err);
      results[tld] = null;
    }
  }
  
  return { data: results };
};

/**
 * Search trademarks for multiple names
 */
export const searchTrademarks = async (params: {
  names: string[];
}) => {
  if (config.useMockData) {
    await mockDelay();
    return { data: mockSearchTrademarks(params) };
  }
  
  return axios.post(`${apiUrl}/search-trademarks`, params);
};

/**
 * Generate a slogan for a given brand name
 */
export const generateSlogan = async (name: string): Promise<string> => {
  if (config.useMockData) {
    await mockDelay();
    return `${name} — built for tomorrow.`;
  }

  const res = await axios.post<{ slogan: string }>(`${apiUrl}/generate-slogan`, { name });
  return res.data.slogan;
};

/**
 * Compare multiple names using AI analysis
 */
export const compareNames = async (params: {
  names: string[];
}) => {
  if (config.useMockData) {
    await mockDelay();
    return {
      data: {
        comparison: params.names.map((name) => ({
          name,
          memorable: Math.floor(Math.random() * 4) + 7,
          brandable: Math.floor(Math.random() * 4) + 7,
          seo: Math.floor(Math.random() * 4) + 7,
          summary: `${name} is a strong, memorable brand name.`,
        })),
        recommendation: params.names[0],
      },
    };
  }

  return axios.post(`${apiUrl}/compare-names`, params);
};

// Export as default for convenience
const api = {
  generateNames,
  generateNamesSimple,
  checkDomain,
  checkDomainsMultipleTLDs,
  searchTrademarks,
  generateSlogan,
  compareNames,
};

export default api;

